import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ValidOption, ValidOptionalInput } from './types';
import { useClickOutside } from '../../hooks/useClickOutside';
import ValidInputError from './ValidInputError';
import ValidErrorMarker from './ValidErrorMarker';
import { IconRemove, IconEmptySquare } from '../Icon';
import ValidInputLabel from './ValidInputLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

export const ALL_SELECTED_LABEL_1 = 'ALL';
export const ALL_SELECTED_LABEL_2 = 'All';
export const ALL_SELECTED_LABEL_3 = 'All options selected';

// Dropdown list sizing
const DESIRED_MAX_LIST_HEIGHT = 400; // px — taller list so more options are visible
const MIN_LIST_HEIGHT = 150; // px — keep it usable even when space is tight
const VIEWPORT_GAP = 12; // px — breathing room from the bottom of the viewport

// VIEW MODEL
export function validPropMultiSelectVM(
    key: string,
    lbl?: string,
    val?: string[],
    def?: string,
    options?: ValidOption[],
): ValidPropMultiSelect {
    return {
        key: key,
        lbl: !!lbl ? lbl : '',
        val: val ?? [],
        def: def || '',
        options: Array.isArray(options) ? options : [],
        err: '',
    };
}

// TYPE
export type ValidPropMultiSelect = {
    key: string;
    lbl: string;
    val: string[];
    def: string;
    options: ValidOption[];
    err: string;
};

type ValidInputMultiSelectProps = ValidOptionalInput & {
    id: string;
    prop: ValidPropMultiSelect;
    // disabled?: boolean
    placeholder?: string;
    someOptionsSelectedPlaceholder?: (options: number) => string;
    allOptionsSelectedPlaceholder?: string;
    onOptionSelect: (vals: (string | number)[]) => void;
    disableAllSelection?: boolean;
    hideLabel?: boolean;
    insertLeftSpacing?: boolean;
    useOptionLabelForComparison?: boolean; // Use when the label and value of the option differs from one another, and you need to compare against the label.
    textInputWidth?: number;
    showClearAllOption?: boolean;
    /** If true, auto-select the only option if there is just one. */
    autoSelectSingleOption?: boolean;
    labelNoMinWidth?: boolean;
    disabled?: boolean;
};

const _ValidInputMultiSelect = ({
    id,
    prop,
    placeholder = 'Select options...',
    someOptionsSelectedPlaceholder,
    allOptionsSelectedPlaceholder,
    onOptionSelect,
    hideLabel,
    checked,
    onCheckboxChange,
    useOptionLabelForComparison = false,
    textInputWidth = 260,
    showClearAllOption = false,
    autoSelectSingleOption = false,
    labelNoMinWidth = false,
    disabled = false,
}: ValidInputMultiSelectProps): JSX.Element => {
    const [expanded, setExpanded] = useState(false);
    // Memoize options for performance
    const memoizedOptions = useMemo(() => prop.options, [prop.options]);
    /** Used for filtering. */
    const [internalListOptions, setInternalListOptions] = useState<ValidOption[]>(memoizedOptions);
    /** Used for selected options only. */
    const [selectedOptions, setSelectedOptions] = useState<(string | number)[]>(prop.val);
    const [filterTerm, setFilterTerm] = useState<string>('');
    const [scrollTop, setScrollTop] = useState(0);
    const [listMaxHeight, setListMaxHeight] = useState(DESIRED_MAX_LIST_HEIGHT);
    const domNode = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLParagraphElement>(null);
    const allSelected = memoizedOptions.length === prop.val.length;
    const showCheckbox = !!onCheckboxChange && checked !== undefined;

    useClickOutside(domNode, () => setExpanded(false));

    useEffect(() => setInternalListOptions(memoizedOptions), [memoizedOptions]);

    useEffect(() => {
        if (
            (prop.val.length === 1 && prop.val[0] === ALL_SELECTED_LABEL_1) ||
            prop.val[0] === ALL_SELECTED_LABEL_2 ||
            prop.val[0] === ALL_SELECTED_LABEL_3
        ) {
            const opts = prop.options.map(opt => opt.val);
            onOptionSelect(opts);
            setSelectedOptions(opts);
        } else {
            setSelectedOptions(prop.val);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [prop.val]);

    // Auto-select the only option if enabled
    useEffect(() => {
        if (autoSelectSingleOption && prop.options.length === 1 && prop.val.length === 0) {
            onOptionSelect([prop.options[0].val]);
        }
    }, [autoSelectSingleOption, prop.options, prop.val, onOptionSelect]);

    // Keep the open list within the viewport: cap its height to the space
    // available below the trigger (never taller than DESIRED_MAX_LIST_HEIGHT).
    useEffect(() => {
        if (!expanded) return;
        const recompute = (): void => {
            const el = triggerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_GAP;
            const next = Math.max(MIN_LIST_HEIGHT, Math.min(spaceBelow, DESIRED_MAX_LIST_HEIGHT));
            setListMaxHeight(next);
        };
        recompute();
        window.addEventListener('resize', recompute);
        window.addEventListener('scroll', recompute, true);
        return (): void => {
            window.removeEventListener('resize', recompute);
            window.removeEventListener('scroll', recompute, true);
        };
    }, [expanded]);

    const totalSelected = prop.val.length;
    const placeholderText = useMemo(() => {
        if (totalSelected === 1) {
            return prop.val[0];
        }
        if (totalSelected === prop.options.length) {
            return allOptionsSelectedPlaceholder ?? ALL_SELECTED_LABEL_3;
        }
        if (totalSelected > 1) {
            return someOptionsSelectedPlaceholder
                ? someOptionsSelectedPlaceholder(totalSelected)
                : `${totalSelected} options selected`;
        }
        return placeholder;
    }, [
        totalSelected,
        placeholder,
        prop.val,
        prop.options.length,
        someOptionsSelectedPlaceholder,
        allOptionsSelectedPlaceholder,
    ]);

    const handleSelectOrDeselectAll = useCallback(
        (event): void => {
            event.stopPropagation();

            if (allSelected) {
                onOptionSelect([]);
                setSelectedOptions([]);
            } else {
                const opts = prop.options.map(opt => opt.val);
                onOptionSelect(opts);
                setSelectedOptions(opts);
            }
        },
        [allSelected, onOptionSelect, prop.options],
    );

    const handleClearAll = useCallback(
        (event): void => {
            event.stopPropagation();

            onOptionSelect([]);
            setSelectedOptions([]);
        },
        [onOptionSelect],
    );

    const handleSelectOrDeselectOption = useCallback(
        (val: string | number): void => {
            const selected = selectedOptions.includes(val);
            const nextSelectedOptions = selected
                ? selectedOptions.filter(opt => opt !== val)
                : [...selectedOptions, val];

            setSelectedOptions(nextSelectedOptions);
            onOptionSelect(nextSelectedOptions);
        },
        [onOptionSelect, selectedOptions],
    );

    const handleToggle = useCallback((): void => {
        if (!disabled) {
            setExpanded(curr => !curr);
        }
    }, [disabled]);

    const handleFilterList = useCallback(
        (event): void => {
            event.stopPropagation();

            const term = event.target.value.toLowerCase();
            const nextItems =
                term === undefined || term === ''
                    ? memoizedOptions
                    : memoizedOptions.filter(opt => {
                          return opt.lbl.toLowerCase().includes(term);
                      });
            setInternalListOptions(nextItems);
            setFilterTerm(term);
        },
        [memoizedOptions],
    );

    const handleCheckboxChange = useCallback((): void => {
        if (showCheckbox) {
            onCheckboxChange(!checked);
        }
    }, [checked, onCheckboxChange, showCheckbox]);

    // Virtualization logic
    const OPTION_HEIGHT = 32; // px, adjust to match your CSS
    const FILTER_INPUT_HEIGHT = 40; // px, adjust to match your filter input's actual height
    // The scrollable area for options is the (viewport-capped) list height minus the filter input height
    const OPTIONS_CONTAINER_HEIGHT = listMaxHeight - FILTER_INPUT_HEIGHT;
    // Always render one extra option to avoid stray whitespace
    const VISIBLE_COUNT = Math.ceil(OPTIONS_CONTAINER_HEIGHT / OPTION_HEIGHT) + 1;

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);
    }, []);

    const totalOptions = internalListOptions.length;
    // Subtract filter input height from scrollTop for virtualization math, clamp to 0
    const adjustedScrollTop = Math.max(0, scrollTop - FILTER_INPUT_HEIGHT);
    const threshold = 0.8; // 80% of option height must be out of view before hiding
    const fractionalIndex = adjustedScrollTop / OPTION_HEIGHT;
    let startIndex = Math.floor(fractionalIndex - threshold);
    if (startIndex < 0) startIndex = 0;
    // For the end, use a similar threshold so the next item appears as soon as the current last is even slightly out of view
    const fractionalEndIndex = (adjustedScrollTop + OPTIONS_CONTAINER_HEIGHT) / OPTION_HEIGHT;
    let endIndex = Math.ceil(fractionalEndIndex + threshold);
    if (endIndex > totalOptions) endIndex = totalOptions;
    // If we're near the end, shift the window up so the dropdown is always filled
    if (endIndex === totalOptions && totalOptions > VISIBLE_COUNT) {
        startIndex = totalOptions - VISIBLE_COUNT;
        if (startIndex < 0) startIndex = 0;
    }
    const topSpacerHeight = startIndex * OPTION_HEIGHT;
    const bottomSpacerHeight = (totalOptions - endIndex) * OPTION_HEIGHT;
    const visibleOptions = internalListOptions.slice(startIndex, endIndex);

    const multiSelect = (
        <div
            id={id}
            className={`drop-select drop-select-v2 drop-row1${disabled ? ' input-disabled' : ''}`}
            style={{ minWidth: textInputWidth ?? 'unset' }}
            ref={domNode}
        >
            <p
                className="drop-select-label"
                onClick={handleToggle}
                ref={triggerRef}
            >
                {placeholderText}
                <button
                    type="button"
                    className="btn-alt"
                >
                    <span className="icon-glyph glyphicon-filter" />
                </button>
            </p>
            <div
                className={`drop-select-list-v2 ${expanded ? 'show-list' : ''}`}
                style={{ maxHeight: expanded ? listMaxHeight : 0, overflowY: 'auto' }}
                onScroll={handleScroll}
            >
                <ul>
                    <li className="multiselect-search">
                        <input
                            type="text"
                            value={filterTerm}
                            onChange={handleFilterList}
                            placeholder="Filter options..."
                        />
                    </li>
                    {showClearAllOption ? (
                        <li
                            className={`clear-all-option${selectedOptions.length > 0 ? '-active' : '-inactive'}`}
                            onClick={handleClearAll}
                        >
                            <IconRemove />
                            <p>Clear All</p>
                        </li>
                    ) : null}
                    {filterTerm || showClearAllOption || internalListOptions.length < 2 ? null : (
                        <li
                            onClick={handleSelectOrDeselectAll}
                            className={`multiselect-selected-option${allSelected ? ' multiselect-option-selected' : ''}`}
                        >
                            <MultiSelectIcon selected={allSelected} />
                            <p>All</p>
                        </li>
                    )}
                    {/* Spacer above visible options */}
                    {topSpacerHeight > 0 && (
                        <li
                            className="dropdown-spacer"
                            style={{
                                height: topSpacerHeight,
                            }}
                            aria-hidden="true"
                        />
                    )}
                    {visibleOptions.map((opt, index) => {
                        let selected = false;
                        if (useOptionLabelForComparison) {
                            selected = prop.val.find(val => val === opt.lbl) ? true : false;
                        } else {
                            selected = prop.val.find(val => val === opt.val) ? true : false;
                        }
                        return (
                            <MultiSelectOption
                                key={`${opt.lbl}-${startIndex + index}`}
                                opt={opt}
                                selected={selected}
                                onClick={handleSelectOrDeselectOption}
                            />
                        );
                    })}
                    {/* Spacer below visible options */}
                    {bottomSpacerHeight > 0 && (
                        <li
                            className="dropdown-spacer"
                            style={{ height: bottomSpacerHeight }}
                            aria-hidden="true"
                        />
                    )}
                </ul>
            </div>
        </div>
    );

    return hideLabel ? (
        multiSelect
    ) : (
        <div className="component-valid-input component-valid-input-multiselect input-multiselect">
            <div className="input-wrapper">
                <ValidInputLabel
                    label={prop.lbl}
                    onClick={showCheckbox ? handleCheckboxChange : undefined}
                    showCheckbox={showCheckbox}
                    checked={checked}
                    noMinWidth={labelNoMinWidth}
                />
                <div className="valid-input">{multiSelect}</div>
                <ValidErrorMarker showMarker={!!prop.err} />
            </div>
            <ValidInputError
                err={prop.err}
                showMsg
            />
        </div>
    );
};

type MultiSelectOptionProps = {
    opt: ValidOption;
    selected: boolean;
    onClick: (val: string | number) => void;
};

const _MultiSelectOption = ({ opt, selected, onClick }: MultiSelectOptionProps): JSX.Element => {
    const handleChange = useCallback(
        (event): void => {
            event.stopPropagation();
            event.preventDefault();

            if (onClick) {
                onClick(opt.val);
            }
        },
        [onClick, opt.val],
    );

    return (
        <li
            className={`multiselect-selected-option${selected ? ' multiselect-option-selected' : ''}`}
            onClick={handleChange}
        >
            <MultiSelectIcon selected={selected} />
            <p>{opt.lbl}</p>
        </li>
    );
};

const MULTISELECT_ICON_SELECTED_COLOR = '#2BB842';

const MultiSelectIcon = ({ selected }: { selected: boolean }): JSX.Element =>
    selected ? (
        <FontAwesomeIcon
            icon={faCheck}
            color={MULTISELECT_ICON_SELECTED_COLOR}
            style={{ strokeWidth: 30, stroke: MULTISELECT_ICON_SELECTED_COLOR, minWidth: 16 }}
        />
    ) : (
        <IconEmptySquare size={14} />
    );

const MultiSelectOption = memo(_MultiSelectOption);
export { MultiSelectOption };

// Custom comparison function for memoization
function areEqual(prevProps: ValidInputMultiSelectProps, nextProps: ValidInputMultiSelectProps): boolean {
    // Only re-render if options or value actually change
    const prevOptions = prevProps.prop.options;
    const nextOptions = nextProps.prop.options;
    const prevVal = prevProps.prop.val;
    const nextVal = nextProps.prop.val;

    // Compare options array reference and length
    if (prevOptions !== nextOptions) {
        if (!prevOptions || !nextOptions || prevOptions.length !== nextOptions.length) return false;
        for (let i = 0; i < prevOptions.length; i++) {
            if (prevOptions[i] !== nextOptions[i]) return false;
        }
    }
    // Compare value array reference and length
    if (prevVal !== nextVal) {
        if (!prevVal || !nextVal || prevVal.length !== nextVal.length) return false;
        for (let i = 0; i < prevVal.length; i++) {
            if (prevVal[i] !== nextVal[i]) return false;
        }
    }
    // Compare other relevant props if needed (add as necessary)
    return true;
}

const ValidInputMultiSelect = memo(_ValidInputMultiSelect, areEqual);
export default ValidInputMultiSelect;
